import { useMyCasesLanding } from '@hooks/useMyCasesLanding';
import { useUserTier } from '@hooks/useUserTier';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { buildOrgRoutePath, buildRoutePath, removeOrgPrefix } from '@utils/routes';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { programService } from '@/services/api';

/**
 * Component that handles navigation to /org-{orgId} URLs.
 * It sets the organization, waits for programs to load, then navigates to the first program.
 */
export const OrgRedirect: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { setSelectedOrganizationId, setSelectedProgramId, setPrograms, selectedOrganizationId } =
    useStore();
  const navigate = useNavigate();

  // Clean the org ID
  const cleanOrgId = orgId ? removeOrgPrefix(orgId) : null;

  // Tier resolves org-wide once the org is set in the store. Tier-2 users go to
  // the org-scoped cases route (/org-{id}/cases); everyone else to their first
  // program's cases. Gate navigation on tier loading so we don't redirect early.
  const { tier, isLoading: tierLoading } = useUserTier();

  // Step 1: Set the organization in the store immediately
  useEffect(() => {
    if (!cleanOrgId) {
      navigate('/');
      return;
    }

    if (cleanOrgId !== selectedOrganizationId) {
      setSelectedOrganizationId(cleanOrgId);
    }
  }, [cleanOrgId, selectedOrganizationId, setSelectedOrganizationId, navigate]);

  // Step 2: Fetch programs AFTER org is set (so the API header has the correct org)
  const { data: programsData, isLoading } = useQuery({
    queryKey: ['programs-for-org', cleanOrgId],
    queryFn: () =>
      programService.getCurrentUserPrograms({
        organization_id: cleanOrgId!,
      }),
    // Only fetch when org is set in store
    enabled: !!cleanOrgId && selectedOrganizationId === cleanOrgId,
  });

  // Users who can only see the cases assigned to them (Case Managers) land on My
  // Cases instead of the first program's cases (BV-2347). Decided against the
  // program they would land in.
  const landingProgramId = programsData?.programs?.[0]?.id ?? null;
  const { isResolving: landingResolving, landOnMyCases } = useMyCasesLanding(
    cleanOrgId,
    landingProgramId,
  );

  // Step 3: Once programs are loaded, set programs and navigate
  useEffect(() => {
    if (!cleanOrgId || selectedOrganizationId !== cleanOrgId) {
      return;
    }

    if (!programsData?.programs || isLoading || tierLoading) {
      return;
    }

    // The API should filter by org using the x-org-id header
    // Programs are already filtered by the API based on the selected org
    const programs = programsData.programs;

    // Wait for the landing decision before touching the store, so the program
    // switch and the navigation still happen in one pass.
    if (tier !== 'tier2' && programs.length > 0 && landingResolving) {
      return;
    }

    setPrograms(programs);

    // Tier-2 users get the org-wide cases page (no program in the URL). Still set
    // the first program in the store so the org page's program-name join + any
    // program-scoped detail navigation have a default context.
    if (tier === 'tier2') {
      if (programs.length > 0 && programs[0].id) {
        setSelectedProgramId(programs[0].id);
      }
      navigate(buildOrgRoutePath('/cases', cleanOrgId), { replace: true });
      return;
    }

    if (programs.length > 0) {
      const firstProgramId = programs[0].id;
      if (firstProgramId) {
        setSelectedProgramId(firstProgramId);
        const path = landOnMyCases
          ? buildOrgRoutePath('/my-cases', cleanOrgId)
          : buildRoutePath('/cases', cleanOrgId, firstProgramId);
        navigate(path, { replace: true });
      }
    } else {
      // No programs available for this org
      setSelectedProgramId(null);
      navigate('/', { replace: true });
    }
  }, [
    programsData,
    isLoading,
    tier,
    tierLoading,
    landingResolving,
    landOnMyCases,
    cleanOrgId,
    selectedOrganizationId,
    setPrograms,
    setSelectedProgramId,
    navigate,
  ]);

  return null;
};
